import { isDeepEqual } from "@suse-o11y/is-deep-equal";
import { isString } from "@suse-o11y/is-string";
import { msPerFrame } from "@suse-o11y/ms-per-frame";
import { useHandler } from "@suse-o11y/use-handler";
import { useMemo } from "@suse-o11y/use-memo";
import debounce from "lodash/debounce";
import React from "react";

import type { Monaco } from "../_monaco";

import type { STQLLanguageContext } from "./_language-stql";
import type { TemplateLanguageVariable } from "./_language-template";

/**
 * It is OK to have the markers fps to be pretty low.
 */
const ON_CHANGE_DEBOUNCE_MS = msPerFrame(5);

/**
 * Create the React.lazy component for each language.
 * This way the language code is only loaded when needed and the loading process
 * is safely handled by React.
 */

// G R O O V Y

const LazyGroovyLanguage = React.lazy(async () => {
	const { registerGroovyLanguage } = await import("./_language-groovy");

	// biome-ignore lint/nursery/noComponentHookFactories: false-positive due to React.lazy - the component is defined once
	const Component: React.FC<{ monaco: typeof Monaco }> = ({ monaco }) => {
		registerGroovyLanguage(monaco);

		return null;
	};

	return { default: Component };
});

// Y A M L

const LazyYamlLanguage = React.lazy(async () => {
	const { yamlToMarkers } = await import("../_utils/yaml-to-markers");

	// biome-ignore lint/nursery/noComponentHookFactories: false-positive due to React.lazy - the component is defined once
	const Component: React.FC<{
		monaco: typeof Monaco;
		editor: Monaco.editor.ICodeEditor;
	}> = ({ monaco, editor }) => {
		React.useEffect(() => {
			const model = editor.getModel();

			if (model != null) {
				const onChange = debounce(() => {
					const markers = yamlToMarkers(monaco, model);
					monaco.editor.setModelMarkers(model, "yaml", markers);
				}, ON_CHANGE_DEBOUNCE_MS);

				const disposable = model.onDidChangeContent(onChange);

				return () => {
					onChange.cancel();
					disposable.dispose();
				};
			}
		}, [editor, monaco]);

		return null;
	};

	return { default: Component };
});

// S T Q L

const LazySTQLLanguage = React.lazy(async () => {
	const { STQL_LANGUAGE_ID, registerStqlLanguage, setStqlLanguageContext, stqlToMarkers } = await import(
		"./_language-stql"
	);

	// biome-ignore lint/nursery/noComponentHookFactories: false-positive due to React.lazy - the component is defined once
	const Component: React.FC<{
		monaco: typeof Monaco;
		editor: Monaco.editor.ICodeEditor;
		getSuggestions: STQLLanguageContext["getSuggestions"];
	}> = ({ monaco, editor, getSuggestions }) => {
		registerStqlLanguage(monaco);

		const stableGetSuggestions = useHandler(getSuggestions);

		React.useEffect(() => {
			setStqlLanguageContext(editor, { getSuggestions: stableGetSuggestions });
		}, [editor, stableGetSuggestions]);

		React.useEffect(() => {
			const model = editor.getModel();

			if (model != null) {
				const onChange = debounce(() => {
					monaco.editor.setModelMarkers(model, STQL_LANGUAGE_ID, stqlToMarkers(monaco, model));
				}, ON_CHANGE_DEBOUNCE_MS);

				const disposable = model.onDidChangeContent(onChange);

				return () => {
					onChange.cancel();
					disposable.dispose();
				};
			}
		}, [editor, monaco]);

		return null;
	};

	return { default: Component };
});

// T E M P L A T E

const LazyTemplateLanguage = React.lazy(async () => {
	const { registerTemplateLanguage, setTemplateLanguageContext } = await import("./_language-template");

	// biome-ignore lint/nursery/noComponentHookFactories: false-positive due to React.lazy - the component is defined once
	const Component: React.FC<{
		monaco: typeof Monaco;
		editor: Monaco.editor.ICodeEditor;
		variables: ReadonlyArray<string | TemplateLanguageVariable>;
	}> = ({ monaco, editor, variables }) => {
		registerTemplateLanguage(monaco);

		const stableVariables = useMemo(
			() => variables.map((variable) => (isString(variable) ? { name: variable } : variable)),
			[variables],
			[isDeepEqual],
		);

		React.useEffect(() => {
			setTemplateLanguageContext(editor, { variables: stableVariables });
		}, [editor, stableVariables]);

		return null;
	};

	return { default: Component };
});

export type EditorLanguage =
	| "yaml"
	| "json"
	| "markdown"
	| "handlebars"
	| "groovy"
	| ({ name: "stql" } & STQLLanguageContext)
	| { name: "template"; variables: ReadonlyArray<string | TemplateLanguageVariable> };

export interface LanguageLoaderProps {
	monaco: typeof Monaco;
	editor: Monaco.editor.ICodeEditor;
	language: EditorLanguage;
}

export const LanguageLoader: React.FC<LanguageLoaderProps> = ({ monaco, editor, language }) => (
	<React.Suspense>
		{language === "groovy" && <LazyGroovyLanguage monaco={monaco} />}

		{language === "yaml" && <LazyYamlLanguage editor={editor} monaco={monaco} />}

		{!isString(language) && language.name === "stql" && (
			<LazySTQLLanguage editor={editor} getSuggestions={language.getSuggestions} monaco={monaco} />
		)}

		{!isString(language) && language.name === "template" && (
			<LazyTemplateLanguage editor={editor} monaco={monaco} variables={language.variables} />
		)}
	</React.Suspense>
);
